// MeetingsSection — Presentational list of already-added business meetings
// for the EditTripModal. The "Add Meeting" action now lives in AddEventSection
// alongside Add Flight/Hotel/Car/Dining, so this section no longer promotes
// itself as a standalone "Business Meetings" widget — it renders NOTHING when
// there are no meetings yet (matching how Flight/Hotel/Car/Dining show no
// placeholder at zero count either), and a slim label + optional "Clear All"
// above the MeetingRow list once meetings exist.
//
// The visibility conditionals come from `getMeetingsSummary(editedTrip)` and
// are passed in as the `summary` prop ({ count, showClearAll, isEmpty }) so
// this component stays render-only. The "Clear All" side effect lives in the
// container, surfaced here as the `onClearAll` callback. Wrapped in
// React.memo. The count label, the render guards (`showClearAll`, `isEmpty`),
// the inline hover handlers, icons (lucide-react), and MeetingRow styles are
// otherwise unchanged from the prior "Business Meetings" section.
//
// Props:
//   summary        - { count, showClearAll, isEmpty } from getMeetingsSummary
//   meetings       - the array of meeting objects to render as rows
//   onClearAll     - () => void invoked by the "Clear All" button
//   onEditMeeting  - (meeting) => void invoked by a row's Edit button
//   onDeleteMeeting- (meetingId) => void invoked by a row's Delete button
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import { Trash2 } from 'lucide-react';
import MeetingRow from './MeetingRow';

export const MeetingsSection = React.memo(function MeetingsSection({
  summary,
  meetings,
  onClearAll,
  onEditMeeting,
  onDeleteMeeting
}) {
  if (summary.isEmpty) return null;

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>
          Meetings ({summary.count})
        </span>
        {summary.showClearAll && (
          <button
            onClick={() => onClearAll()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              backgroundColor: 'white',
              color: '#dc2626',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#fef2f2';
              e.target.style.borderColor = '#f87171';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.borderColor = '#fecaca';
            }}
            title="Delete all meetings"
          >
            <Trash2 size={14} />
            Clear All
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {meetings.map((meeting, _index) => (
          <MeetingRow
            key={meeting.id}
            meeting={meeting}
            onEdit={() => onEditMeeting(meeting)}
            onDelete={() => onDeleteMeeting(meeting.id)}
          />
        ))}
      </div>
    </div>
  );
});

export default MeetingsSection;
